import { Injectable, WritableSignal } from '@angular/core';

import { Observable, Subject, firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

import { AppSettingsService } from '@core/services/app-settings/app-settings.service';

import { BaseCommandWithSettings } from '@shared/commands/base-command-with-settings';
import { IProfileCommand, IProfileCommandContext, ProfileData } from '@shared/models/profile-command.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

/**
 * Базовый класс для всех команд профиля
 *
 * Предоставляет общую функциональность:
 * - Проверка доступности команды
 * - Интеграция с серверными настройками
 * - Поддержка команд-переключателей с сохранением состояния
 * - Управление чекбоксами
 *
 * @example
 * ```typescript
 * export class NotificationCommand extends BaseProfileCommand {
 *   readonly type = 'enableNotification';
 *   readonly title = 'Push-уведомления';
 *   readonly checkbox = 'checked';
 *   readonly settingsNames = ['NOTIFICATION_ENABLED'];
 *
 *   async execute(context: IProfileCommandContext): Promise<void> {
 *     const newState = !this.checkboxChecked;
 *     await this.setSetting('notificationEnabled', newState);
 *     this.updateCheckboxState(newState);
 *   }
 * }
 * ```
 */
@Injectable()
export abstract class BaseProfileCommand extends BaseCommandWithSettings<ProfileData> implements IProfileCommand {
  /** Текущее состояние чекбокса */
  readonly checkboxChecked?: WritableSignal<boolean>;

  /** Отключен ли чекбокс */
  readonly checkboxDisabled?: WritableSignal<boolean>;

  public abstract readonly checkbox?: 'unchecked' | 'checked' | 'disabled' | null;
  public abstract readonly group?: string;
  public abstract hidden?: boolean;
  public abstract readonly icon?: string;
  public abstract readonly order?: number;
  public abstract readonly settingsNames?: string[];
  public abstract readonly subtitle?: WritableSignal<string>;
  public abstract readonly title: string;
  public abstract readonly type: string;

  constructor(
    protected override commandAvailabilityService?: CommandAvailabilityService,
    protected appSettingsService?: AppSettingsService
  ) {
    super(commandAvailabilityService);
  }

  /**
   * Базовая проверка доступности команды
   */
  public isAvailable(context: IProfileCommandContext): Observable<boolean> | boolean {
    // Выполняем дополнительные проверки команды
    const additionalCheck = this.checkAdditionalAvailability(context);

    // Используем базовую логику проверки серверных настроек
    return this.checkAvailabilityWithSettings(context.data, additionalCheck);
  }

  /**
   * Дополнительные проверки доступности для конкретной команды
   * Переопределяется в наследниках для специфичных проверок
   */
  protected checkAdditionalAvailability(_context: IProfileCommandContext): Observable<boolean> | boolean | undefined {
    return true;
  }

  /**
   * Получает настройку из AppSettingsService
   * По умолчанию использует this.type как ключ настройки
   */
  protected async getSetting<T>(key?: string, defaultValue?: T): Promise<T> {
    const settingKey = key || this.type;

    if (this.appSettingsService) {
      const setting = await firstValueFrom(this.appSettingsService.getSetting<T>(settingKey));

      return setting !== null ? setting : (defaultValue as T);
    }

    return defaultValue as T;
  }

  /**
   * Сохраняет настройку в AppSettingsService
   * По умолчанию использует this.type как ключ настройки
   */
  protected async setSetting(value: unknown, key?: string): Promise<void> {
    const settingKey = key || this.type;

    if (this.appSettingsService) {
      return this.appSettingsService.setSetting(settingKey, value).toPromise();
    }
  }

  /**
   * Выполняет базовое переключение состояния чекбокса с сохранением настройки
   * Может быть переопределено в наследниках для дополнительной логики
   */
  protected async executeToggle(): Promise<boolean> {
    const newState = !this.checkboxChecked();

    // Сохраняем настройку
    await this.setSetting(newState);

    // Обновляем состояние чекбокса
    this.updateCheckboxState(newState);

    return newState;
  }

  /**
   * Обновляет состояние чекбокса
   */
  protected updateCheckboxState(checked: boolean, disabled?: boolean): void {
    this.checkboxChecked.set(checked);
    if (disabled !== undefined) {
      this.checkboxDisabled.set(disabled);
    }
  }

  // /** Явное уведомление об изменениях (когда меняются не чекбоксы, а, например, подзаголовок) */
  // protected notifyStateChanged(): void {
  //   this._stateChanged.next(null);
  // }

  /**
   * Выполнение команды - должно быть реализовано в наследниках
   */
  public abstract execute(context: IProfileCommandContext): Observable<unknown> | Promise<unknown> | unknown;
}
